/**
 * The functional administrator's export templates for ONE screen — saved for
 * their organization only (the tenant DB; another organization running the
 * same application never sees them).
 *
 * Left: "Your organization" (editable) and "Standard" (shipped with the app,
 * read-only, "Copy to change"). Right: the TemplateEditor with the engine's
 * live preview, and Save / Discard / Delete. A save carries the version it
 * was read at, so two administrators cannot silently overwrite each other.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Copy, Loader2, Plus, X } from 'lucide-react'
import { ErrorNote, Note, Pill, meta, overlay, windowFrame, windowHeader } from '../../messaging/ui'
import { tw } from '../../testHooks'
import { Z_EXPORT_TEMPLATES } from '../../layers'
import { TemplateEditor } from './TemplateEditor'
import type { ExportTemplate, ExportTransport, ManageTemplates } from './types'
import { blankTemplate, cleanTemplate, exportErrorText, layoutSummary } from './types'

export function TemplateManager({ transport, objName, onClose, startWith, notify }: {
  transport: ExportTransport
  objName: string
  onClose: (changed: boolean) => void
  startWith?: string
  notify?: { success: (m: string) => void; error: (m: string) => void }
}) {
  const [data, setData] = useState<ManageTemplates | null>(null)
  const [loadError, setLoadError] = useState<{ message: string; problems: string[] } | null>(null)
  const [selected, setSelected] = useState<string | null>(startWith || null)
  const [draft, setDraft] = useState<ExportTemplate | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<{ message: string; problems: string[] } | null>(null)
  const [changed, setChanged] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = useCallback(async (select?: string | null) => {
    try {
      const d = await transport.manageTemplates(objName)
      setData(d)
      setLoadError(null)
      const all = [...d.org, ...d.standard]
      const pick = all.find(t => t.id === (select ?? selected)) || all[0] || null
      setSelected(pick?.id || null)
      setDraft(pick ? JSON.parse(JSON.stringify(pick)) : null)
      setIsNew(false)
      setDirty(false)
    } catch (err) {
      setLoadError(exportErrorText(err))
    }
  }, [transport, objName, selected])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(startWith) }, [transport, objName])

  const current = useMemo(() => {
    if (!data || !selected) return null
    return [...data.org, ...data.standard].find(t => t.id === selected) || null
  }, [data, selected])
  const readOnly = !draft || draft.scope === 'standard'

  const pick = (t: ExportTemplate) => {
    if (dirty && !window.confirm(`Discard your unsaved changes to “${draft?.name}”?`)) return
    setSelected(t.id); setDraft(JSON.parse(JSON.stringify(t))); setIsNew(false); setDirty(false)
    setError(null); setConfirmDelete(false)
  }
  const startNew = () => {
    if (!data) return
    const t = blankTemplate(data.catalog, data.screen)
    setSelected(t.id); setDraft({ ...t, scope: 'org' }); setIsNew(true); setDirty(true); setError(null)
  }
  const copy = () => {
    if (!draft) return
    const t: ExportTemplate = { ...cleanTemplate(draft), id: `org-${Date.now().toString(36)}`,
      name: `${draft.name} (copy)`.slice(0, 80), is_default: false, scope: 'org' }
    setSelected(t.id); setDraft(t); setIsNew(true); setDirty(true); setError(null)
  }
  const save = async () => {
    if (!draft) return
    setSaving(true); setError(null)
    try {
      const saved = await transport.saveTemplate(objName, cleanTemplate(draft), isNew ? null : (current?.version ?? null))
      setChanged(true)
      notify?.success('Saved for your organization. Other organizations do not see this template.')
      await load(saved.id)
    } catch (err) {
      setError(exportErrorText(err))
    } finally {
      setSaving(false)
    }
  }
  const remove = async () => {
    if (!draft || isNew) return
    setSaving(true); setError(null)
    try {
      await transport.deleteTemplate(objName, draft.id)
      setChanged(true)
      notify?.success('Template deleted.')
      setConfirmDelete(false)
      await load(null)
    } catch (err) {
      setError(exportErrorText(err))
    } finally {
      setSaving(false)
    }
  }
  const close = () => {
    if (dirty && !window.confirm('Close without saving your changes?')) return
    onClose(changed)
  }
  const previewDraft = useCallback(
    (d: ExportTemplate) => transport.preview({ obj: objName, draft: cleanTemplate(d) }),
    [transport, objName])

  const orgList = data ? [...data.org, ...(isNew && draft ? [draft] : [])] : []

  return (
    <div style={{ ...overlay, zIndex: Z_EXPORT_TEMPLATES }} role="dialog" aria-modal="true" aria-label="Export templates" {...tw('screen', 'export-templates')}>
      <div style={{ ...windowFrame, maxWidth: 1360 }}>
        <div style={windowHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 600 }}>Export templates{data ? ` · ${data.screen}` : ''}</div>
            <div style={meta}>Pick the columns, name the headings and choose how lines are laid out.
              Saved for your organization only.</div>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={close} aria-label="Close"
            {...tw('action', 'export-templates-close')}><X size={15} /></button>
        </div>
        {loadError && <div style={{ padding: 16 }}><ErrorNote error={loadError} /></div>}
        {!data && !loadError && (
          <div style={{ ...meta, padding: 24, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Loader2 size={15} className="spin" /> Loading templates…
          </div>
        )}
        {data && (
          <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: 0, flex: 1 }}>
            <nav aria-label="Templates" style={{ flex: '0 1 280px', minWidth: 220,
              borderRight: '1px solid var(--color-border, #d8dee6)', overflow: 'auto' }}>
              <div style={{ padding: 12 }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={startNew}
                  disabled={!data.org_templates} {...tw('action', 'export-template-new')}>
                  <Plus size={14} /> New template
                </button>
                {!data.org_templates && <p style={{ ...meta, margin: '8px 0 0' }}>The designer turned organization templates off for this screen.</p>}
              </div>
              <Group title="Your organization" />
              {orgList.map(t => <Item key={t.id} t={t} on={t.id === selected} onClick={() => pick(t)} />)}
              {!orgList.length && <p style={{ ...meta, padding: '6px 14px', margin: 0 }}>None yet.</p>}
              <Group title="Standard (shipped with the app)" />
              {data.standard.map(t => <Item key={t.id} t={t} on={t.id === selected} onClick={() => pick(t)} />)}
              {!data.standard.length && <p style={{ ...meta, padding: '6px 14px', margin: 0 }}>None.</p>}
            </nav>
            <div style={{ flex: '1 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {!draft && <div style={{ padding: 20 }}><Note>Choose a template, or make a new one.</Note></div>}
              {draft && (
                <>
                  <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                      <span style={{ fontSize: 16, fontWeight: 600 }}>{draft.name || 'New template'}</span>
                      {readOnly ? <Pill>Standard · read only</Pill> : <Pill tone="success">Your organization</Pill>}
                      {dirty && <Pill tone="warning">Unsaved changes</Pill>}
                      {readOnly && (
                        <button type="button" className="btn btn-primary btn-sm" onClick={copy} disabled={!data.org_templates}
                          {...tw('action', 'export-template-copy')}><Copy size={14} /> Copy to change</button>
                      )}
                    </div>
                    {readOnly && <div style={{ marginBottom: 12 }}><Note tone="accent">This template comes with the app and is the same for every organization. Copy it to make your own version.</Note></div>}
                    {(draft.missing?.length || 0) > 0 && (
                      <div style={{ marginBottom: 12 }}><Note tone="warning">These columns are no longer on the screen and are left out of exports: {draft.missing!.join(', ')}. Remove them and save.</Note></div>
                    )}
                    <TemplateEditor template={draft} catalog={data.catalog} formats={data.formats}
                      readOnly={readOnly}
                      onChange={(t) => { setDraft(t); setDirty(true) }}
                      onPreview={previewDraft} />
                  </div>
                  {!readOnly && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      flexWrap: 'wrap', padding: '12px 16px', borderTop: '1px solid var(--color-border, #d8dee6)',
                      background: 'var(--color-surface-2, #f0f3f7)' }}>
                      <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                        {error ? <ErrorNote error={error} /> : <span style={meta}>Saved for your organization only. It does not change the app.</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {!isNew && !confirmDelete && (
                          <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(true)}
                            disabled={saving} {...tw('action', 'export-template-delete')}>Delete</button>
                        )}
                        {confirmDelete && (
                          <>
                            <span style={meta}>Delete “{draft.name}”?</span>
                            <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>Keep it</button>
                            <button type="button" className="btn btn-danger" onClick={() => void remove()} disabled={saving}
                              {...tw('action', 'export-template-delete-confirm')}>Delete</button>
                          </>
                        )}
                        <button type="button" className="btn btn-secondary" disabled={!dirty || saving}
                          onClick={() => { if (isNew) void load(null); else { setDraft(JSON.parse(JSON.stringify(current))); setDirty(false); setError(null) } }}>
                          Discard changes
                        </button>
                        <button type="button" className="btn btn-primary" disabled={saving || !dirty}
                          onClick={() => void save()} {...tw('action', 'export-template-save')}>
                          {saving ? <Loader2 size={14} className="spin" /> : null} Save template
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Group({ title }: { title: string }) {
  return (
    <div style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #465062)',
      background: 'var(--color-surface-2, #f0f3f7)', borderTop: '1px solid var(--color-border, #e6eaef)',
      borderBottom: '1px solid var(--color-border, #e6eaef)' }}>{title}</div>
  )
}

function Item({ t, on, onClick }: { t: ExportTemplate; on: boolean; onClick: () => void }) {
  const cols = t.header.length + t.details.reduce((n, d) => n + d.columns.length, 0)
  return (
    <button type="button" onClick={onClick} aria-current={on ? 'true' : undefined}
      {...tw('action', `export-template:${t.id}`)}
      style={{ display: 'grid', gap: 2, width: '100%', textAlign: 'left', padding: '10px 14px', border: 0,
        borderBottom: '1px solid var(--color-border, #e6eaef)', cursor: 'pointer', font: 'inherit',
        background: on ? 'var(--color-accent-weak, rgba(0,97,208,.10))' : 'transparent',
        color: 'var(--color-text, #1b2330)' }}>
      <span style={{ fontWeight: 600, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {t.name || 'New template'}
        {t.is_default && <Pill tone="accent">Default</Pill>}
        {t.roles.length > 0 && <Pill>{t.roles.join(', ')}</Pill>}
        {(t.missing?.length || 0) > 0 && <Pill tone="warning">Needs attention</Pill>}
      </span>
      <span style={meta}>{layoutSummary(t)} · {cols} columns</span>
    </button>
  )
}
